import { createBrowserRouter, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { RouteErrorBoundary } from '@/components/common/RouteErrorBoundary';
import { useAuthStore } from '@/stores/authStore';
import { ProfileDialog } from '@/components/profile/ProfileDialog';
import { LegalConsentGate } from '@/components/auth/LegalConsentGate';
import DashboardPage from '@/features/dashboard/DashboardPage';
import CalendarPage from '@/features/calendar/CalendarPage';
import ReleaseApprovalPage from '@/features/release-approval/ReleaseApprovalPage';
import WorkOrdersPage from '@/features/workorders/WorkOrdersPage';
import WorkOrderDetailPage from '@/features/workorders/WorkOrderDetailPage';
import WorkOrderWindowPage from '@/features/workorders/WorkOrderWindowPage';
import InboxDocumentsPage from '@/features/inbox/InboxDocumentsPage';
import PartiesPage from '@/features/parties/PartiesPage';
import PersonDetailPage from '@/features/persons/PersonDetailPage';
import CompanyDetailPage from '@/features/companies/CompanyDetailPage';
import VehicleTabsPage from '@/features/vehicles/VehicleTabsPage';
import VehicleDetailPage from '@/features/vehicles/VehicleDetailPage';
import InspectionsPage from '@/features/inspections/InspectionsPage';
import InspectionDetailPage from '@/features/inspections/InspectionDetailPage';
import MobileInspectionPage from '@/features/inspections/mobile/MobileInspectionView';
import ApprovalsPage from '@/features/approvals/ApprovalsPage';
import ExceptionsPage from '@/features/exceptions/ExceptionsPage';
import InventoryPage from '@/features/inventory/InventoryPage';
import MessagesPage from '@/features/messages/MessagesPage';
import ConversationPage from '@/features/messages/ConversationPage';
import { TenantModuleRoute } from '@/components/layout/TenantModuleRoute';
import ReportsPage from '@/features/reports/ReportsPage';
import ReportBuilderPage from '@/features/reports/ReportBuilderPage';
import InvoicesPage from '@/features/invoices/InvoicesPage';
import InvoiceDetailPage from '@/features/invoices/InvoiceDetailPage';
import AccountingExportPage from '@/features/invoices/AccountingExportPage';
import PaymentMatchingPage from '@/features/invoices/PaymentMatchingPage';
import { CardTerminalExceptions } from '@/features/invoices/CardTerminalExceptions';
import { SveaSettlementExceptions } from '@/features/invoices/SveaSettlementExceptions';
import { INVOICE_READ_PERMISSIONS } from '@/lib/invoicePermissions';
import SettingsPage from '@/features/settings/SettingsPage';
import OnboardingPage from '@/features/onboarding/OnboardingPage';
import MyTasksPage from '@/features/tasks/MyTasksPage';
import TaskDetailPage from '@/features/tasks/TaskDetailPage';
import HRPage from '@/features/hr/HRPage';
import LoginPage from '@/features/auth/LoginPage';
import HandoffPage from '@/features/auth/HandoffPage';
import SignInErrorPage from '@/features/auth/SignInErrorPage';
import WorkshopSelectionPage from '@/features/auth/WorkshopSelectionPage';
import AcceptInvitationPage from '@/features/auth/AcceptInvitationPage';
import ForgotPasswordPage from '@/features/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/features/auth/ResetPasswordPage';
import RequestDemoPage from '@/features/demo/RequestDemoPage';
import VerifyDemoRequestPage from '@/features/demo/VerifyDemoRequestPage';
import SignupPage from '@/features/signup/SignupPage';
import SignupVerifyPage from '@/features/signup/SignupVerifyPage';
import SignupProgressPage from '@/features/signup/SignupProgressPage';
import MobileJoinPage from '@/features/mobile-join/MobileJoinPage';
import NotFoundPage from '@/features/errors/NotFoundPage';

// Protected route wrapper
function ProtectedRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const forcePasswordChange = useAuthStore((s) => s.user?.forcePasswordChange);
  const navigate = useNavigate();
  const location = useLocation();
  const [showForcedDialog, setShowForcedDialog] = useState(true);

  if (!isAuthenticated) {
    const target = location.pathname + location.search + location.hash;
    useAuthStore.getState().setRedirectAfterLogin(target);
    return <Navigate to="/login" replace />;
  }

  const handleForcedPasswordChanged = () => {
    setShowForcedDialog(false);
    void navigate('/', { replace: true });
  };

  if (forcePasswordChange) {
    return (
      <AppShell>
        <ProfileDialog
          open={showForcedDialog}
          onClose={() => {/* Cannot close forced dialog */}}
          forced
          onForcedPasswordChanged={handleForcedPasswordChanged}
        />
      </AppShell>
    );
  }

  // After the forced password change above: terms are asked on first login and
  // whenever the configured version changes (#3014).
  return (
    <LegalConsentGate>
      <AppShell>
        <Outlet />
      </AppShell>
    </LegalConsentGate>
  );
}

// Permission-gated route wrapper (redirects to dashboard if denied)
function PermissionRoute({ permission, anyOf, children }: {
  permission?: string;
  anyOf?: string[];
  children: ReactNode;
}) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);

  let allowed = false;
  if (permission) allowed = hasPermission(permission);
  else if (anyOf) allowed = hasAnyPermission(anyOf);
  else allowed = true;

  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}


// Redirects a legacy detail route (e.g. /persons/:id) to its new nested
// location under /parties, preserving the dynamic id segment — <Navigate
// to="..."> can't interpolate a route param into a static string.
function RedirectWithId({ to }: { to: (id: string) => string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? to(id) : '/'} replace />;
}

// The quote queue moved to the work orders page's Quotes tab (#3788); old
// links keep their ?bucket= filter.
function QuotesRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'quotes');
  return <Navigate to={{ pathname: '/workorders', search: `?${params.toString()}` }} replace />;
}

function AutomationsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'automations');
  return <Navigate to={{ pathname: '/settings', search: `?${params.toString()}`, hash: location.hash }} replace />;
}

// Public route wrapper (redirects if already authenticated)
function PublicRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <PublicRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [{ index: true, element: <LoginPage /> }],
  },
  {
    // Where a Paya ID sign-in lands (#1625). Deliberately NOT wrapped in
    // `PublicRoute`: that redirects an authenticated session to `/`, and this
    // page's whole job is to *become* authenticated — the redirect would fire
    // on the re-render right after the session lands and race the navigate.
    path: '/login/handoff',
    errorElement: <RouteErrorBoundary />,
    element: <HandoffPage />,
  },
  {
    // Where a refused or broken Paya ID sign-in lands (#1951). Deliberately NOT
    // wrapped in `PublicRoute`: a step-up can fail for someone who already holds
    // a session, and bouncing them to `/` would swallow the explanation.
    path: '/login/error',
    errorElement: <RouteErrorBoundary />,
    element: <SignInErrorPage />,
  },
  {
    // Workshop picker after a Paya ID sign-in that named more than one
    // servable tenant (#2052). Not wrapped in `PublicRoute`: the person is not
    // authenticated yet, and this page's job is to finish that sign-in.
    path: '/login/workshops',
    errorElement: <RouteErrorBoundary />,
    element: <WorkshopSelectionPage />,
  },
  {
    path: '/accept-invitation',
    errorElement: <RouteErrorBoundary />,
    element: <AcceptInvitationPage />,
  },
  {
    // The mobile enrollment landing page (#1244). Deliberately NOT wrapped in
    // `PublicRoute`: that redirects an authenticated session to `/`, and an admin
    // who is signed in to the portal on their own laptop is exactly who might
    // open this link to hand it to their phone. It needs no session either way —
    // everything it renders comes out of the URL fragment.
    path: '/m/join',
    errorElement: <RouteErrorBoundary />,
    element: <MobileJoinPage />,
  },
  {
    path: '/forgot-password',
    errorElement: <RouteErrorBoundary />,
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    errorElement: <RouteErrorBoundary />,
    element: <ResetPasswordPage />,
  },
  {
    path: '/request-demo',
    errorElement: <RouteErrorBoundary />,
    element: <RequestDemoPage />,
  },
  {
    path: '/request-demo/verify',
    errorElement: <RouteErrorBoundary />,
    element: <VerifyDemoRequestPage />,
  },
  // Self-service signup (#3017). All public and deliberately outside
  // `ProtectedRoute`: there is no session yet, and the checkout capability they
  // use is not an auth token. Not wrapped in `PublicRoute` either — a signed-in
  // person opening the mail link must still get the explanation, not a bounce.
  {
    path: '/signup',
    errorElement: <RouteErrorBoundary />,
    element: <SignupPage />,
  },
  {
    path: '/signup/verify',
    errorElement: <RouteErrorBoundary />,
    element: <SignupVerifyPage />,
  },
  // The two return URLs Stripe Checkout is given by the API (contract §3.3).
  {
    path: '/signup/checkout/success',
    errorElement: <RouteErrorBoundary />,
    element: <SignupProgressPage source="success" />,
  },
  {
    path: '/signup/checkout/cancel',
    errorElement: <RouteErrorBoundary />,
    element: <SignupProgressPage source="cancel" />,
  },
  {
    path: '/signup/status',
    errorElement: <RouteErrorBoundary />,
    element: <SignupProgressPage source="status" />,
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <DashboardPage /> },
      // Calendar consolidates the work-order, shift and lift calendars into
      // one page with URL-driven tabs. Shifts has no dedicated read
      // permission (its calendar is open to any authenticated user), so this
      // route is intentionally ungated — the page itself clamps the visible
      // tabs to work_orders.read (work orders) / settings.view (lifts).
      { path: 'calendar', element: <CalendarPage /> },
      {
        path: 'workorders',
        element: <PermissionRoute permission="work_orders.read"><WorkOrdersPage /></PermissionRoute>,
      },
      {
        path: 'workorders/:id',
        element: <PermissionRoute permission="work_orders.read"><WorkOrderDetailPage /></PermissionRoute>,
      },
      // Landing page of the second work order window (#3653).
      {
        path: 'work-order-window',
        element: <PermissionRoute permission="work_orders.read"><WorkOrderWindowPage /></PermissionRoute>,
      },
      {
        path: 'inbox/documents',
        element: <PermissionRoute permission="work_orders.read"><InboxDocumentsPage /></PermissionRoute>,
      },
      // Quote queue (#2652) is the Quotes tab of /workorders since #3788.
      {
        path: 'quotes',
        element: <PermissionRoute permission="work_orders.read"><QuotesRedirect /></PermissionRoute>,
      },
      // Customers consolidates the Persons and Companies registries into one
      // page with URL-driven tabs (?tab=persons|companies, persons default).
      // Detail routes live one level deeper than the old top-level
      // /persons/:id and /companies/:id.
      {
        path: 'parties',
        element: <PermissionRoute permission="customers.read"><Outlet /></PermissionRoute>,
        children: [
          { index: true, element: <PartiesPage /> },
          { path: 'persons/:id', element: <PersonDetailPage /> },
          { path: 'companies/:id', element: <CompanyDetailPage /> },
        ],
      },
      // Legacy routes redirect to the consolidated view / nested detail routes.
      // `/customers` was this route until #833 B4. The legacy customers model is
      // gone, so the URL now names what the page actually shows — persons and
      // companies, i.e. work-order parties. Existing bookmarks keep working.
      { path: 'customers', element: <PermissionRoute permission="customers.read"><Navigate to="/parties" replace /></PermissionRoute> },
      { path: 'customers/persons/:id', element: <PermissionRoute permission="customers.read"><RedirectWithId to={(id) => `/parties/persons/${id}`} /></PermissionRoute> },
      { path: 'customers/companies/:id', element: <PermissionRoute permission="customers.read"><RedirectWithId to={(id) => `/parties/companies/${id}`} /></PermissionRoute> },
      { path: 'persons', element: <PermissionRoute permission="customers.read"><Navigate to="/parties" replace /></PermissionRoute> },
      { path: 'persons/:id', element: <PermissionRoute permission="customers.read"><RedirectWithId to={(id) => `/parties/persons/${id}`} /></PermissionRoute> },
      { path: 'companies', element: <PermissionRoute permission="customers.read"><Navigate to="/parties?tab=companies" replace /></PermissionRoute> },
      { path: 'companies/:id', element: <PermissionRoute permission="customers.read"><RedirectWithId to={(id) => `/parties/companies/${id}`} /></PermissionRoute> },
      {
        // The fleet and the make list are one page with URL-driven tabs
        // (?tab=vehicles|makes, vehicles default) — #2201.
        path: 'vehicles',
        element: <PermissionRoute permission="vehicles.read"><Outlet /></PermissionRoute>,
        children: [
          { index: true, element: <VehicleTabsPage /> },
          { path: ':id', element: <VehicleDetailPage /> },
        ],
      },
      {
        path: 'inspections',
        element: (
          <PermissionRoute permission="inspections.read">
            <TenantModuleRoute module="inspections">
              <Outlet />
            </TenantModuleRoute>
          </PermissionRoute>
        ),
        children: [
          { index: true, element: <InspectionsPage /> },
          { path: ':id', element: <InspectionDetailPage /> },
          { path: ':id/mobile', element: <MobileInspectionPage /> },
        ],
      },
      { path: 'approvals', element: <PermissionRoute permission="approvals.read"><ApprovalsPage /></PermissionRoute> },
      { path: 'exceptions', element: <PermissionRoute permission="scheduling.exceptions"><ExceptionsPage /></PermissionRoute> },
      // Inventory Management consolidates Stock, Parts needs, Purchase orders and
      // Transit warehouse into one page with URL-driven tabs. `anyOf` lets a user
      // with only purchase_orders.read reach the Purchase orders tab; the page
      // itself clamps the visible tabs to the user's permissions.
      { path: 'inventory', element: <PermissionRoute anyOf={['inventory.read', 'purchase_orders.read']}><InventoryPage /></PermissionRoute> },
      // Legacy routes redirect to their consolidated tab so existing links/bookmarks stay intact.
      { path: 'purchaseorders', element: <PermissionRoute permission="purchase_orders.read"><Navigate to="/inventory?tab=purchaseorders" replace /></PermissionRoute> },
      { path: 'transit-warehouse', element: <PermissionRoute permission="inventory.read"><Navigate to="/inventory?tab=transit" replace /></PermissionRoute> },
      { path: 'parts-dashboard', element: <PermissionRoute permission="inventory.read"><Navigate to="/inventory?tab=needs" replace /></PermissionRoute> },
      {
        path: 'messages',
        element: (
          // Customer portal chats need no WhatsApp integration (#2830).
          <PermissionRoute permission="messages.read">
            <Outlet />
          </PermissionRoute>
        ),
        children: [
          { index: true, element: <MessagesPage /> },
          { path: ':customerId', element: <ConversationPage /> },
        ],
      },
      {
        path: 'reports',
        element: <PermissionRoute permission="reports.read"><Outlet /></PermissionRoute>,
        children: [
          { index: true, element: <ReportsPage /> },
          { path: 'builder', element: <ReportBuilderPage /> },
        ],
      },
      { path: 'invoices', element: <PermissionRoute anyOf={[...INVOICE_READ_PERMISSIONS]}><InvoicesPage /></PermissionRoute> },
      { path: 'invoices/exports', element: <PermissionRoute anyOf={['reports.read', 'invoices.manage']}><AccountingExportPage /></PermissionRoute> },
      { path: 'invoices/card-terminal-reconciliation', element: <PermissionRoute anyOf={[...INVOICE_READ_PERMISSIONS]}><CardTerminalExceptions /></PermissionRoute> },
      { path: 'invoices/svea-settlements', element: <PermissionRoute anyOf={[...INVOICE_READ_PERMISSIONS]}><SveaSettlementExceptions /></PermissionRoute> },
      { path: 'invoices/payments', element: <PermissionRoute permission="invoices.manage"><PaymentMatchingPage /></PermissionRoute> },
      { path: 'invoices/:id', element: <PermissionRoute anyOf={[...INVOICE_READ_PERMISSIONS]}><InvoiceDetailPage /></PermissionRoute> },
      { path: 'tasks', element: <MyTasksPage /> },
      { path: 'tasks/:id', element: <TaskDetailPage /> },
      // Shift Patterns now lives on the HR page (issue #1013) — Shifts as a
      // standalone page/nav entry is gone. This legacy route keeps old
      // links/bookmarks working. Unlike /shifts (previously ungated), the
      // new destination requires hr.view_team like the rest of HR — an
      // intentional tightening specified by the issue, not a preservation
      // of the old open access.
      { path: 'shifts', element: <PermissionRoute permission="hr.view_team"><Navigate to="/hr?tab=shift-patterns" replace /></PermissionRoute> },
      { path: 'hr', element: <PermissionRoute permission="hr.view_team"><HRPage /></PermissionRoute> },
      // Settings now also hosts the former Equipment page's sections (issue
      // #1012) as tabs. This legacy route keeps old links/bookmarks working
      // by redirecting to the equivalent Settings tab, under the same
      // settings.view gate the Equipment page used to have.
      { path: 'equipment', element: <PermissionRoute permission="settings.view"><Navigate to="/settings?tab=equipment" replace /></PermissionRoute> },
      { path: 'settings', element: <PermissionRoute anyOf={['settings.view', 'automation.rules.read']}><SettingsPage /></PermissionRoute> },
      { path: 'automations', element: <PermissionRoute permission="automation.rules.read"><AutomationsRedirect /></PermissionRoute> },
      // Customer approval of a test version (#3316). Every signed-in user sees the
      // state; the page itself refuses outside a test environment.
      { path: 'release', element: <ReleaseApprovalPage /> },
      // First-run guidance for a new workshop (#1853). Inside the shell rather
      // than full-screen: the wizard is never mandatory, so leaving it must
      // always be one click away.
      { path: 'onboarding', element: <PermissionRoute permission="settings.view"><OnboardingPage /></PermissionRoute> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
